import { BaseReportParsedData, MoneyDataItem } from '../../interfaces/reports/BaseReport.interface';

export interface TableToPdfOptions {
    columns: Array<string>;
    data: Array<Array<string | number>>;
    fileName: string;
    title?: string;
    subtitle?: string;
    orientation?: 'p' | 'l'; // portrait or landscape
    pageSize?: string; // 'a4', 'letter', etc.
}

const VERTICAL_SORT_ORDER = [
    'sales plan',
    'markdowns plan',
    'inventory bom plan',
    'inventory deviation',
    'inventory deviation cost',
    'inflow plan retail',
    'inflow plan cost',
    'on order retail eom',
    'on order cost eom',
    'on order retail',
    'on order cost',
    'inflow trend',
    'inflow cost trend',
    'inflow updated',
    'inflow cost updated',
    'otb trend',
    'otb cost trend',
    'otb updated',
    'otb cost updated',
    'cum. otb trend',
    'cum. otb cost trend',
    'cum. otb updated',
    'cum. otb cost updated',
    'otb retail',
    'otb cost',
    'cum. otb',
    'cum. otb cost',
    'retail',
    'cost',
];

const ALL_VERTS = [
    'locationCode',
    'classCode',
    'category',
    'parentClientClassCode',
    'month',
    "vertical",
];

const ALWAYS_KEEP_FIELDS = [
    'locationName',
    'className',
    'costComplement',
    'costComplementMtd',
    'categorySortOrder',
]

function compareNumericLikeStrings(a: string, b: string): number | null {
    const aTrimmed = a.trim();
    const bTrimmed = b.trim();
    const aIsNumeric = /^\d+$/.test(aTrimmed);
    const bIsNumeric = /^\d+$/.test(bTrimmed);

    if (aIsNumeric && bIsNumeric) {
        const aNum = Number(aTrimmed);
        const bNum = Number(bTrimmed);
        if (aNum !== bNum) {
            return aNum - bNum;
        }
        // Same numeric value (e.g. 0700 vs 700): keep deterministic order by raw string.
        return aTrimmed.localeCompare(bTrimmed);
    }

    if (aIsNumeric && !bIsNumeric) return -1;
    if (!aIsNumeric && bIsNumeric) return 1;
    return null;
}

function groupByHierarchy<T>(
    data: T[],
    hierarchy: string[],
    skipSort = false,
): T[] {
    if (!data.length) return [];

    // Detect all MoneyDataItem fields dynamically
    const first = data[0] as any;
    const moneyFields = Object.keys(first).filter(
        key => first[key] instanceof MoneyDataItem
    );

    // Detect other non-hierarchy fields
    const otherFields = Object.keys(first).filter(
        key => !ALL_VERTS.includes(key) && !(first[key] instanceof MoneyDataItem) && !ALWAYS_KEEP_FIELDS.includes(key)
    );

    type Row = Record<string, any>;
    const result: Row[] = [];

    // Helper: Sum money fields for a group of rows
    function sumMoneyFields(rows: Row[]): Row {
        const sums: Row = {};
        for (const field of moneyFields) {
            let total = 0;
            for (const row of rows) {
                const val = row[field];
                if (val instanceof MoneyDataItem) {
                    total += Number(val.value) || 0;
                }
            }
            sums[field] = new MoneyDataItem(total);
        }
        return sums;
    }

    function makeTotalRow(vertValues: Record<string, string | null>, groupRows: Row[]): Row {
        const row: Row = {};
        // Hierarchy/vert columns: set value or ""
        for (const vert of ALL_VERTS) {
            row[vert] = vertValues[vert] ?? "";
        }
        // Money fields: sum
        Object.assign(row, sumMoneyFields(groupRows));
        // Always keep fields: copy from first row
        for (const field of ALWAYS_KEEP_FIELDS) {
            row[field] = groupRows[0][field] || "";
        }
        // Non-vert, non-money fields: null
        for (const field of otherFields) {
            row[field] = null;
        }
        return row;
    }

    // Recursive grouping, sorts at every level
    function recursiveGroup(rows: Row[], depth: number, prefix: Record<string, string | null>) {
        result.push(makeTotalRow(prefix, rows));

        if (depth < hierarchy.length) {
            const key = hierarchy[depth];

            // Default grouping for other fields
            const groups: Record<string, Row[]> = {};
            for (const row of rows) {
                const groupVal = row[key] || '';
                if (!groups[groupVal]) groups[groupVal] = [];
                groups[groupVal].push(row);
            }
            let sortedKeys = Object.keys(groups);
            for (const groupKey of sortedKeys) {
                const nextPrefix = { ...prefix, [key]: groupKey };
                recursiveGroup(groups[groupKey], depth + 1, nextPrefix);
            }
        }
    }

    // Start recursive grouping from top
    recursiveGroup(data as unknown as Row[], 0, {});

    // sort result. "TOTAL" rows (empty hierarchy values) always on top within their group
    // Other hierarchy keys are sorted descending.
    // "vertical" is sorted ascending to group them.
    result.sort((a, b) => {
        // Always sort by hierarchy keys first, then by vertical
        const hierarchyKeys = hierarchy.filter(h => h !== 'vertical');

        for (const key of hierarchyKeys) {
            const valA = (a[key] || '').toString().toLowerCase();
            const valB = (b[key] || '').toString().toLowerCase();

            if (valA === '' && valB !== '') return -1; // a is TOTAL, comes first
            if (valA !== '' && valB === '') return 1;  // b is TOTAL, comes first

            // Special-case: when sorting the category hierarchy, prefer numeric categorySortOrder
            if (key === 'category') {
                const isNaCategory = (value: string) => {
                    const normalized = value.trim().toLowerCase();
                    return normalized === '' || normalized === 'n/a' || normalized === 'uncategorized';
                };

                const aIsNa = isNaCategory(valA);
                const bIsNa = isNaCategory(valB);
                if (aIsNa && !bIsNa) return 1;
                if (!aIsNa && bIsNa) return -1;

                // Check either row for the presence of categorySortOrder (not just `a`)
                if (a.categorySortOrder !== undefined || b.categorySortOrder !== undefined) {
                    const aOrder = a.categorySortOrder != null ? Number(a.categorySortOrder) : null;
                    const bOrder = b.categorySortOrder != null ? Number(b.categorySortOrder) : null;

                    if (aOrder != null && bOrder != null) {
                        if (aOrder < bOrder) return -1;
                        if (aOrder > bOrder) return 1;
                        continue;
                    }
                    
                    // If null, move to end
                    if (aOrder == null && bOrder != null) return 1;
                    if (aOrder != null && bOrder == null) return -1;
                    // both null -> fallback to string compare below
                    if (valA > valB) return 1;
                    if (valA < valB) return -1;
                    continue;
                }

                if (valA > valB) return 1;
                if (valA < valB) return -1;
                continue;
            }

            // Other hierarchy keys use existing string comparison behavior
            if (key === 'classCode' || key === 'parentClientClassCode') {
                const numericComparison = compareNumericLikeStrings(valA, valB);
                if (numericComparison !== null) {
                    if (numericComparison < 0) return -1;
                    if (numericComparison > 0) return 1;
                    continue;
                }
            }

            if (valA > valB) return 1;
            if (valA < valB) return -1;
        }

        // After sorting by hierarchy, sort by vertical if it's in the hierarchy
        if (hierarchy.includes('vertical')) {
            const valA = (a.vertical || '').toString().toLowerCase();
            const valB = (b.vertical || '').toString().toLowerCase();

            if (valA === '' && valB !== '') return -1;
            if (valA !== '' && valB === '') return 1;

            const indexA = VERTICAL_SORT_ORDER.indexOf(valA);
            const indexB = VERTICAL_SORT_ORDER.indexOf(valB);

            if (indexA !== indexB) {
                return indexA - indexB;
            }
        }

        return 0;
    });

    return result as T[];
}

class OtbRecapReportService {
    /**
     * Format a number as a percent (no decimals, with % sign)
     */
    static formatPercent(value: number | string): string {
        const num = Number(value);
        if (isNaN(num)) return '';
        return `${Math.round(num)}%`;
    }

    /**
     * Main "rollup" function. Returns a flat array with total/subtotal rows at all levels.
     * Data columns are detected as MoneyDataItem instances.
     * Non-vert, non-MoneyDataItem columns are set to null in total/subtotal rows.
     */
    static groupByHierarchy<T extends BaseReportParsedData>(
        data: Array<T>,
        hierarchy: Array<string>,
        skipSort = false,
    ): Array<T> {
        return groupByHierarchy<T>(
            data,
            hierarchy,
            skipSort,
        );
    }

    static hierarchyItemToVerticalDisplayNames<T extends BaseReportParsedData>(
        row: T,
        hierarchy: Array<string>,
    ): Array<string> {
        hierarchy = hierarchy.filter(h => ALL_VERTS.includes(h));
        // move 'vertical' to end if present
        if (hierarchy.includes('vertical')) {
            hierarchy = hierarchy.filter(h => h !== 'vertical');
            hierarchy.push('vertical');
        }
        let labels = hierarchy.map(h => {
            const key = h.toLowerCase();
            switch (key) {
                case 'locationcode':
                    return row.locationCode === '' ? 'TOTAL' : `${row.locationCode} ${row.locationName}`;
                case 'classcode':
                    return row.classCode === '' ? 'TOTAL' : `${row.classCode} ${row.className}`;
                case 'category':
                    return row.category === '' ? 'TOTAL' : row.category || '';
                case 'parentclientclasscode':
                    if (row.parentClientClassCode === null) {
                        // use classcode
                        return row.classCode === '' ? 'TOTAL' : `${row.classCode} ${row.className}`;
                    } else {
                        return row.parentClientClassCode === '' ? 'TOTAL' : `${row.parentClientClassCode}`;
                    }
                case 'vertical':
                    return row.vertical === '' ? 'TOTAL' : row.vertical || '';
                default:
                    return '';
            }
        });

        return labels;
    }

    /**
     * Returns table row style class name based on hierarchy and row data's first N items.
     * 
     * Applies styling based on how many 'TOTAL' entries are present in the first N items (N = hierarchy.length).
     * - If all are 'TOTAL': primary
     * - If all but one: medium-light
     * - If all but two: light
     * - Otherwise: null
     * 
     * @param row 
     * @param hierarchy 
     */
    static getRowClassName(row: Array<string | number>, hierarchy: Array<string>): string | null {
        const N = hierarchy.length;
        if (N <= 1) {
            return null;
        }

        // Check first N items for 'TOTAL'
        let totalCount = 0;
        for (let i = 0; i < N; i++) {
            const val = row[i];
            if (typeof val === 'string' && ['total',].includes(val.toLowerCase())) {
                totalCount++;
            }
        }

        if (totalCount === 0) {
            return null;
        }

        if (totalCount === N) {
            return 'font-bold bg-tw-primary text-white hover:bg-tw-primary-medium-light';
        }
        if (totalCount === N - 1) {
            return 'font-bold bg-tw-primary-medium-light text-white hover:bg-tw-primary-light';
        }
        if (totalCount === N - 2) {
            return 'font-bold bg-tw-primary-light text-white hover:bg-tw-primary';
        }
        return null;
    }

    static sortAndFilterData<T extends BaseReportParsedData>
    (
        data: Array<T>,
        hierarchy: string[],
        locationFilter: string[],
        classFilter: string[],
        categoryFilter: string[],
        skipSort = false,
    ): Array<T> {
        // Filter data based on selected filters
        let filteredData = data.filter(item => {
            const matchesLocation = locationFilter.length === 0 || locationFilter.includes(item.locationCode);
            const matchesClass = hierarchy.includes('parentClientClassCode') ?
                (classFilter.length === 0 || classFilter.includes(item.classCode) || (item.parentClientClassCode && classFilter.includes(item.parentClientClassCode)))
                : (classFilter.length === 0 || classFilter.includes(item.classCode));
            const matchesCategory = categoryFilter.length === 0 || (item.category !== undefined && categoryFilter.includes(item.category));
            return matchesLocation && matchesClass && matchesCategory;
        });

    
        // Sort data based on hierarchy
        return OtbRecapReportService.groupByHierarchy(filteredData, hierarchy, skipSort);
    }
}

export default OtbRecapReportService;
